import { useCallback, useRef, useSyncExternalStore } from "react";

/** Subscribe to the media clock with a stable snapshot for Safari. */
export function useAudioCurrentTime(nativeAudio: HTMLAudioElement | null) {
  const currentTimeRef = useRef(0);

  return useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) => {
        const onTimeChange = () => {
          // useSyncExternalStore requires getSnapshot to return a stable value
          // until the store changes. Safari can return a different currentTime
          // on consecutive reads, so capture it at the media event rather than
          // reading it from getSnapshot.
          currentTimeRef.current = nativeAudio?.currentTime ?? 0;
          onStoreChange();
        };

        nativeAudio?.addEventListener("emptied", onTimeChange);
        nativeAudio?.addEventListener("loadedmetadata", onTimeChange);
        nativeAudio?.addEventListener("seeking", onTimeChange);
        nativeAudio?.addEventListener("seeked", onTimeChange);
        nativeAudio?.addEventListener("timeupdate", onTimeChange);

        onTimeChange();

        return () => {
          nativeAudio?.removeEventListener("emptied", onTimeChange);
          nativeAudio?.removeEventListener("loadedmetadata", onTimeChange);
          nativeAudio?.removeEventListener("seeking", onTimeChange);
          nativeAudio?.removeEventListener("seeked", onTimeChange);
          nativeAudio?.removeEventListener("timeupdate", onTimeChange);
        };
      },
      [nativeAudio],
    ),
    () => currentTimeRef.current,
  );
}
